// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import catalogStyles from '../../bank-upload/styles/institution-catalog.module.css';
import styles from './institution-form.module.css';
import { AppIcon, Input, Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Controller } from 'react-hook-form';
import { useInstitutionForm } from './use-institution-form';

export const InstitutionForm = defineComponent({
  displayName: 'InstitutionForm',
  useSetup: useInstitutionForm,
  View: ({
    formId,
    onSubmit,
    control,
    handleSubmit,
    t,
    logoUrl,
    hasLogoUrl,
    isDrawerLayout,
  }) => (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
        <div
          className={classNames(
            styles['manual-form-row'],
            isDrawerLayout && styles['manual-form-row-drawer'],
          )}
        >
          <div
            className={classNames(
              styles['logo-aside-wrapper'],
              isDrawerLayout && 'self-start',
            )}
          >
            <div
              className={classNames(
                styles['logo-aside'],
                isDrawerLayout && styles['logo-aside-drawer'],
              )}
              data-testid="bank-upload-institution-logo-preview"
            >
              {hasLogoUrl ? (
                <img src={logoUrl} alt="" className={styles['logo-aside-image']} />
              ) : (
                <AppIcon icon="FileImage" color="disabled" size="2xl" />
              )}
            </div>
          </div>

          <div
            className={classNames(
              styles['manual-fields'],
              isDrawerLayout && styles['manual-fields-drawer'],
            )}
          >
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="institution-manual-name-input"
                    required
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.institution.name"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="institution-manual-name-input"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder={t(
                      'aggregator.create-bank.institution.name-placeholder',
                      { ns: 'aggregator' },
                    )}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="logoUrl"
              render={({ field, fieldState }) => (
                <div className={catalogStyles['field-stack']}>
                  <Label
                    htmlFor="institution-manual-logo-url-input"
                    error={!!fieldState.error}
                  >
                    <Translation
                      i18nKey="aggregator.create-bank.institution.logo-url"
                      ns="aggregator"
                    />
                  </Label>
                  <Input
                    id="institution-manual-logo-url-input"
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    placeholder="https://"
                  />
                  {fieldState.error ? (
                    <output
                      htmlFor="institution-manual-logo-url-input"
                      className={styles['field-error']}
                      data-testid="institution-logo-validation-error"
                    >
                      <Translation
                        i18nKey="aggregator.create-bank.institution.logo-invalid"
                        ns="aggregator"
                      />
                    </output>
                  ) : null}
                </div>
              )}
            />
          </div>
        </div>
      </fieldset>
    </form>
  ),
});
